import { computed, type Ref, type ComputedRef } from 'vue';
import type { SchemaConflict } from './useSchemaImport';

export function useSchemaValidation(
  editableSchema: { appellation: string; type: string; description: string },
  schemaConflict: Ref<SchemaConflict | null> | ComputedRef<SchemaConflict | null>
): {
  validateSchemaType: (type: string | undefined) => string;
  isSchemaValid: ComputedRef<boolean>;
  validationErrors: ComputedRef<string[]>;
  validSchemaTypes: Array<{ value: string; label: string }>;
} {
  
  // Validation du type de schéma
  const validateSchemaType = (type: string | undefined): string => {
    const validTypes = ['Acquisition', 'Processing', 'Analyse', 'Autre'];
    if (!type) return '';
    
    if (validTypes.includes(type)) return type;
    
    const lowerType = type.toLowerCase();
    const match = validTypes.find(validType => 
      validType.toLowerCase().includes(lowerType) || lowerType.includes(validType.toLowerCase())
    );
    
    return match || '';
  };

  // Validation complète du schéma
  const isSchemaValid = computed((): boolean => {
    const basicValidation = editableSchema.appellation.trim() !== '' && editableSchema.type !== '';
    
    // Si il y a un conflit de schéma et que ce n'est pas un UUID match, l'utilisateur doit choisir une action
    if (schemaConflict.value && schemaConflict.value.type === 'name_match') {
      return basicValidation && schemaConflict.value.action !== undefined;
    }
    
    return basicValidation;
  });

  // Messages d'erreur de validation
  const validationErrors = computed((): string[] => {
    const errors: string[] = [];
    
    if (!editableSchema.appellation.trim()) {
      errors.push('Le nom du schéma est obligatoire');
    }
    
    if (!editableSchema.type) {
      errors.push('Le type du schéma est obligatoire');
    }
    
    if (schemaConflict.value && schemaConflict.value.type === 'name_match' && !schemaConflict.value.action) {
      errors.push('Vous devez choisir une action pour le schéma existant');
    }
    
    return errors;
  });

  // Types de schémas valides
  const validSchemaTypes = [
    { value: 'Acquisition', label: 'Acquisition' },
    { value: 'Processing', label: 'Processing' },
    { value: 'Analyse', label: 'Analyse' },
    { value: 'Autre', label: 'Autre' }
  ];

  return {
    validateSchemaType,
    isSchemaValid,
    validationErrors,
    validSchemaTypes
  };
} 